// Keep in sync with talent-review-templates.norms.ts on the API.
// The norm distribution of a template describes which share of the population
// is expected to end up in every cell, so analytics can compare the actual
// distribution against it. The shares are whole percentages that always add up
// to this total.
export const TOTAL_NORM = 100;

type NormCell = { norm?: number };

/**
 * Spreads 100 percent over `count` cells. The remainder is handed out one
 * point at a time, so the norms always add up to exactly 100.
 * @param count Number of cells to spread the norm distribution over.
 */
export function evenNorms(count: number) {
	if (count <= 0) {
		return [];
	}

	const base = Math.floor(TOTAL_NORM / count);
	const remainder = TOTAL_NORM - base * count;

	return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

export function totalNorm(cells: NormCell[][]) {
	return cells.reduce(
		(total, row) => row.reduce((rowTotal, cell) => rowTotal + (cell.norm ?? 0), 0) + total,
		0,
	);
}

export function distributeEvenly<Cell>(cells: Cell[][]): (Cell & { norm: number })[][] {
	const norms = evenNorms(cells.reduce((count, row) => count + row.length, 0));

	let index = 0;

	return cells.map((row) =>
		row.map((cell) => {
			const norm = norms[index];
			index += 1;

			return { ...cell, norm };
		}),
	);
}
